import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import logger from "@/utils/logger";

export interface SubscriptionDetails {
  user_id: string;
  stripe_customer_id: string;
  stripe_subscription_id: string;
  plan_id: string;
  plan_type: string;
  status: string;
  current_period_end: string;
  cancel_at_period_end: boolean;
}

export interface UseSubscriptionStatusResult {
  hasActiveSubscription: boolean | null;
  subscriptionDetails: SubscriptionDetails | null;
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

/**
 * Hook to check if the current user has an active subscription
 * @returns Object containing subscription status and details
 */
// Cache for subscription status to reduce frequent checks
const subscriptionCache = {
  userId: null as string | null,
  hasActiveSubscription: null as boolean | null,
  subscriptionDetails: null as SubscriptionDetails | null,
  lastChecked: 0,
  cacheLifetime: 5 * 60 * 1000, // 5 minutes cache lifetime
};

interface UseSubscriptionStatusOptions {
  skipCache?: boolean;
  forceLoadingState?: boolean;
}

export function useSubscriptionStatus(options: UseSubscriptionStatusOptions = { skipCache: false }): UseSubscriptionStatusResult {
  const { user } = useAuth();
  const [hasActiveSubscription, setHasActiveSubscription] = useState<
    boolean | null
  >(null);
  const [subscriptionDetails, setSubscriptionDetails] =
    useState<SubscriptionDetails | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const retryCount = useRef(0);
  const maxRetries = 3;

  // Check if we can use cached data
  const canUseCache = useMemo(() => {
    if (options.skipCache) return false;
    if (!user || !subscriptionCache.userId || user.id !== subscriptionCache.userId) return false;
    
    const now = Date.now();
    const cacheAge = now - subscriptionCache.lastChecked;
    return cacheAge < subscriptionCache.cacheLifetime;
  }, [user, options.skipCache]);

  // Initialize from cache if available
  useEffect(() => {
    if (canUseCache && user && user.id === subscriptionCache.userId) {
      logger.info("Using cached subscription status");
      setHasActiveSubscription(subscriptionCache.hasActiveSubscription);
      setSubscriptionDetails(subscriptionCache.subscriptionDetails);
      setIsLoading(false);
    }
  }, [canUseCache, user]);

  const checkSubscription = useCallback(
    async (forceRefresh = false) => {
      if (forceRefresh) {
        // Reset retry count on manual refresh
        retryCount.current = 0;
      }

      setIsLoading(true);
      setError(null);

      // Don't check if user is not logged in or if we can use cache
      if (!user || (!forceRefresh && canUseCache)) {
        logger.info("No user found, skipping subscription check");
        setHasActiveSubscription(false);
        setSubscriptionDetails(null);
        setIsLoading(false);
        return;
      }

      try {
        logger.info(
          `Checking subscription status for user: ${user.id} (attempt ${retryCount.current + 1})`
        );

        // Use the has_active_subscription RPC function
        const { data: hasActive, error: subscriptionError } = await (
          supabase as any
        ).rpc("has_active_subscription", { p_user_id: user.id });

        if (subscriptionError) {
          logger.error("Error checking subscription:", subscriptionError);
          setError(
            `Failed to check subscription status: ${subscriptionError.message}`
          );
          setHasActiveSubscription(false);

          // Retry logic for transient errors
          if (retryCount.current < maxRetries) {
            logger.info(
              `Retrying subscription check (${retryCount.current + 1}/${maxRetries})`
            );
            retryCount.current += 1;
            setTimeout(() => checkSubscription(), 1000 * retryCount.current); // Exponential backoff
            return;
          }
        } else {
          // Clear any cached data first
          const currentHasActive = !!hasActive;

          // Reset retry count on success
          retryCount.current = 0;

          // Log subscription status change
          if (hasActiveSubscription !== currentHasActive) {
            logger.info(
              `Subscription status changed from ${hasActiveSubscription} to ${currentHasActive}`
            );
          }

          // Update subscription status
          setHasActiveSubscription(currentHasActive);
          
          // Update cache
          subscriptionCache.userId = user.id;
          subscriptionCache.hasActiveSubscription = currentHasActive;
          subscriptionCache.lastChecked = Date.now();
          logger.info(
            `User subscription status: ${currentHasActive ? "Active" : "Inactive"}`
          );

          // If user has a subscription, get the details
          if (currentHasActive) {
            const { data: details, error: detailsError } = await (
              supabase as any
            ).rpc("get_user_subscription", { p_user_id: user.id });

            if (detailsError) {
              logger.error(
                "Error fetching subscription details:",
                detailsError
              );
              setError(
                `Failed to fetch subscription details: ${detailsError.message}`
              );
            } else if (details && details.length > 0) {
              const subscriptionDetails = details[0];
              setSubscriptionDetails(subscriptionDetails);
              
              // Update cache with subscription details
              subscriptionCache.subscriptionDetails = subscriptionDetails;
              
              logger.info("Subscription details loaded:", subscriptionDetails);
            } else {
              logger.info(
                "No subscription details found despite active status"
              );
            }
          } else {
            // Clear subscription details if no active subscription
            setSubscriptionDetails(null);
            subscriptionCache.subscriptionDetails = null;
          }
        }
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : String(err);
        logger.error("Unexpected error checking subscription:", err);
        setError(`An unexpected error occurred: ${errorMessage}`);
        setHasActiveSubscription(false);

        // Retry on unexpected errors
        if (retryCount.current < maxRetries) {
          logger.info(
            `Retrying after error (${retryCount.current + 1}/${maxRetries})`
          );
          retryCount.current += 1;
          setTimeout(() => checkSubscription(), 1000 * retryCount.current);
        }
      } finally {
        setIsLoading(false);
      }
    },
    [user, canUseCache]
  );

  // Check subscription when user changes or when cache can't be used
  useEffect(() => {
    if (user && !canUseCache) {
      checkSubscription();
    }
  }, [user, canUseCache, checkSubscription]);

  // If forceLoadingState is true, override the isLoading state
  // This is useful during login transitions to prevent flashing of subscription required page
  const effectiveIsLoading = options.forceLoadingState === true ? true : isLoading;
  
  return {
    hasActiveSubscription,
    subscriptionDetails,
    isLoading: effectiveIsLoading,
    error,
    refetch: () => checkSubscription(true),
  };
}
